'use client';

import { useEffect, useId, useState } from 'react';
import dynamic from 'next/dynamic';
import { Box, Skeleton } from '@mui/material';
import { useTheme } from 'next-themes';

import { ExternalLinkConfirmDialog, useExternalLinkConfirm } from '@/components/chat/external-link-confirm';
import { sanitizeMarkdownHtml } from '@/libs/markdown/markdown-config';
import { configureCourseMarkdown } from '@/libs/markdown/markdown-viewer-config';
import type { MdPreviewProps } from 'md-editor-rt';

const MdPreview = dynamic(
  async () => {
    const [editorModule, mermaidModule, katexModule] = await Promise.all([
      import('md-editor-rt'),
      import('mermaid'),
      import('katex'),
    ]);
    editorModule.config({
      editorExtensions: {
        mermaid: { instance: mermaidModule.default },
        katex: { instance: katexModule.default },
      },
      markdownItConfig: configureCourseMarkdown,
    });
    return editorModule.MdPreview;
  },
  {
    ssr: false,
    loading: () => <Skeleton variant='rounded' height={320} />,
  },
);

export type MarkdownViewerProps = {
  content: string;
  id?: string;
  className?: string;
  compact?: boolean;
};

const isExternalUrl = (href: string) => {
  try {
    return new URL(href, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
};

export function MarkdownViewer({ content, id, className, compact = false }: MarkdownViewerProps) {
  const generatedId = useId();
  const previewId = id ?? `markdown-viewer-${generatedId.replace(/:/g, '')}`;
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const confirmation = useExternalLinkConfirm();

  useEffect(() => {
    setMounted(true);
  }, []);

  const theme = mounted && resolvedTheme === 'dark' ? 'dark' : 'light';
  const classes = ['markdown-viewer', compact ? 'markdown-viewer-compact' : null, className].filter(Boolean).join(' ');

  return (
    <>
      <Box
        className={classes}
        onClick={(event) => {
          const target = event.target as HTMLElement;
          const anchor = target.closest('a');
          if (anchor?.href && isExternalUrl(anchor.href)) {
            event.preventDefault();
            confirmation.request(anchor.href);
          }
        }}
      >
        <MdPreview
          id={previewId}
          value={content}
          theme={theme as MdPreviewProps['theme']}
          language='en-US'
          previewTheme='default'
          sanitize={sanitizeMarkdownHtml}
          noImgZoomIn={false}
        />
      </Box>
      <ExternalLinkConfirmDialog
        url={confirmation.pendingUrl ?? ''}
        open={confirmation.pendingUrl !== null}
        onClose={confirmation.close}
        onConfirm={confirmation.confirm}
      />
    </>
  );
}
